import React, { useState } from 'react';
import { Check, Star, Crown, Zap, Shield, Users, Target, Clock } from 'lucide-react';
import Button from '../../components/common/Button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const SubscriptionPricingCard = ({ plan, isPopular = false, onSubscribe }) => {
  const {
    id,
    name,
    description,
    price,
    currency,
    features,
    target_role
  } = plan;

  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  // Generic feature icon mapping
  const getFeatureIcon = (featureText) => {
    const iconMap = {
      cv_processing: Zap,
      profile_boost: Star,
      priority_support: Crown,
      basic_support: Users,
      job_applications: Target,
      unlimited: Shield,
      premium: Crown,
      basic: Check,
      analytics: Users,
      search: Target,
      placement: Crown,
      posting: Check,
    };

    // Try exact match first
    const lowerText = featureText.toLowerCase();

    if (iconMap[lowerText]) {
      return iconMap[lowerText];
    }

    // Try partial matches for dynamic features
    if (lowerText.includes('support')) return Users;
    if (lowerText.includes('priority')) return Crown;
    if (lowerText.includes('boost')) return Star;
    if (lowerText.includes('processing')) return Zap;
    if (lowerText.includes('application')) return Target;
    if (lowerText.includes('analytics')) return Users;
    if (lowerText.includes('search')) return Target;
    if (lowerText.includes('placement')) return Crown;
    if (lowerText.includes('posting')) return Check;
    if (lowerText.includes('unlimited')) return Shield;

    // Default icon
    return Check;
  };

  // Parse features based on data type
  const parseFeatures = () => {
    if (!features) return [];

    if (typeof features === 'string') {
      return features
        .split(',')
        .map(feature => feature.trim())
        .filter(feature => feature.length > 0)
        .map(feature => ({
          key: feature.toLowerCase().replace(/\s+/g, '_'),
          label: feature,
          icon: getFeatureIcon(feature),
          isBoolean: true
        }));
    }

    if (typeof features === 'object' && !Array.isArray(features)) {
      return Object.entries(features)
        .filter(([_, value]) => value !== false && value !== null && value !== undefined)
        .map(([key, value]) => ({
          key,
          value,
          label: formatFeatureLabel(key, value),
          icon: getFeatureIcon(key),
          isBoolean: typeof value === 'boolean',
          isNumeric: typeof value === 'number'
        }));
    }

    // Fallback for arrays or other types
    return [];
  };

  const formatFeatureLabel = (key, value) => {
    // Convert snake_case to Title Case
    const words = key.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    );
    let label = words.join(' ');

    // Handle numeric values
    if (typeof value === 'number') {
      if (key.includes('application') || key.includes('job')) {
        label = `${value} Job Applications`;
      } else if (key.includes('posting')) {
        label = `${value} Job Postings`;
      } else {
        label = `${value} ${label}`;
      }
    }

    return label;
  };

  // Generate feature list
  const featureList = parseFeatures();

  const handleGetStarted = async () => {
    setIsLoading(true);
    try {
      navigate('/pricing/payment', {
        state: {
          plan: {
            id,
            name,
            price,
            currency,
            description
          }
        }
      });
    } catch (error) {
      toast.error('Error navigating to payment page:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:scale-105 ${isPopular
        ? 'border-primary-500 shadow-primary-100'
        : 'border-gray-200'
      }`}>
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg flex items-center gap-1">
            <Star className="w-4 h-4" />
            Most Popular
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{name}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>

        {/* Price */}
        <div className="text-center mb-6">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-gray-900 text-lg">Ksh</span>
            <span className="text-4xl font-bold text-gray-900">{price}</span>
            <span className="text-gray-500 text-sm">/{currency === 'USD' || currency === '$' ? 'month' : currency}</span>
          </div>
          <p className="text-gray-500 text-sm mt-1">per month</p>
        </div>

        {/* Features List */}
        <div className="space-y-3 mb-8">
          {featureList.length === 0 ? (
            <p className="text-gray-500 text-sm text-center">No features listed</p>
          ) : (
            featureList.map((feature) => (
              <div key={feature.key} className="flex items-center gap-3">
                <div className={`p-1 rounded-full ${feature.isNumeric
                    ? 'bg-primary-100 text-primary-600'
                    : feature.isBoolean === false
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-green-100 text-green-600'
                  }`}>
                  <feature.icon className="w-4 h-4" />
                </div>
                <span className="text-gray-700 text-sm flex-1">
                  {feature.label}
                </span>
                {feature.isNumeric && (
                  <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full font-medium">
                    {feature.value}
                  </span>
                )}
                {feature.isBoolean && feature.value === true && (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                    Included
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Subscribe Button */}
        <Button
          onClick={handleGetStarted}
          disabled={isLoading}
          fullWidth
          className={`py-3 font-semibold text-base ${isPopular
              ? 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg'
              : 'bg-gray-900 hover:bg-gray-800'
            }`}
        >
          {isLoading ? 'Redirecting...' : 'Get Started'}
        </Button>

        {/* Target Role Badge */}
        <div className="text-center mt-4">
          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full capitalize">
            {target_role}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPricingCard;